import type {LoadingSize} from '../loading';
import type {ButtonHTMLAttributes, ReactNode} from 'react';

import {cn} from '../../helpers/cn';
import {Loading} from '../loading';

export type ButtonVariant = 'fill' | 'outline' | 'ghost' | 'link';
export type ButtonColor = 'primary' | 'secondary' | 'destructive' | 'success';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  /** Disables the click/focus wave animation (e.g. static icon buttons). */
  disableWave?: boolean;
};

const disabledFillClasses =
  'border-disabled-border! bg-disabled-surface! text-disabled-fg! shadow-none! hover:border-disabled-border! hover:bg-disabled-surface! hover:text-disabled-fg!';

const disabledOutlineClasses =
  'border-disabled-border! bg-white! text-disabled-fg! hover:border-disabled-border! hover:bg-white! hover:text-disabled-fg!';

const disabledTextClasses =
  'border-transparent! bg-transparent! text-disabled-fg! hover:bg-transparent! hover:text-disabled-fg!';

const baseClasses =
  'relative inline-flex cursor-pointer items-center justify-center gap-1.5 overflow-visible font-iransans font-normal transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange disabled:cursor-not-allowed';

const waveButtonClasses = 'ds-wave-btn';

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 min-h-8 rounded-[6px] px-[11px] text-[12px] leading-[18px]',
  md: 'h-10 min-h-10 rounded-[8px] px-[15px] text-[14px] leading-[21px]',
  lg: 'h-12 min-h-12 rounded-[12px] px-[26px] text-[16px] leading-6'
};

const linkSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 min-h-8 px-[11px] py-0 text-[14px] leading-[21px]',
  md: 'h-10 min-h-10 px-[15px] py-0 text-[14px] leading-[21px]',
  lg: 'h-12 min-h-12 px-[26px] py-0 text-[16px] leading-6'
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'size-4',
  md: 'size-4',
  lg: 'size-4'
};

const loadingSizeMap: Record<ButtonSize, LoadingSize> = {
  sm: 'small',
  md: 'small',
  lg: 'small'
};

const variantColorClasses: Record<ButtonVariant, Record<ButtonColor, string>> = {
  fill: {
    primary:
      'border border-transparent bg-orange text-white shadow-[0_2px_0_0] shadow-orange/10 hover:bg-orange-light-01 active:bg-orange-dark-01',
    secondary:
      'border border-disabled-border bg-white text-neutral-fg shadow-[0_2px_0_0] shadow-gray-10/5 hover:border-orange hover:text-orange active:border-orange-dark-01 active:text-orange-dark-01',
    destructive:
      'border border-transparent bg-destructive text-white shadow-[0_2px_0_0] shadow-destructive/10 hover:bg-red-light-02 active:bg-destructive-heavy',
    success:
      'border border-transparent bg-success text-success-on shadow-[0_2px_0_0] shadow-gray-10/5 hover:bg-success-heavy active:bg-success-heavy'
  },
  outline: {
    primary:
      'border border-orange bg-transparent text-orange hover:bg-orange-wash active:bg-orange-pale',
    secondary:
      'border border-disabled-border bg-transparent text-neutral-fg hover:border-orange hover:text-orange',
    destructive:
      'border border-destructive bg-transparent text-destructive hover:bg-red-light-09 active:bg-red-light-08',
    success:
      'border border-success bg-transparent text-success hover:bg-success-surface active:bg-success-surface'
  },
  ghost: {
    primary:
      'border border-transparent bg-transparent text-orange hover:bg-orange-wash active:bg-orange-pale',
    secondary:
      'border border-transparent bg-transparent text-neutral-fg hover:bg-neutral-surface active:bg-neutral-surface-heavy',
    destructive:
      'border border-transparent bg-transparent text-destructive hover:bg-red-light-09 active:bg-red-light-08',
    success:
      'border border-transparent bg-transparent text-success hover:bg-success-surface active:bg-success-surface'
  },
  link: {
    primary:
      'border border-transparent bg-transparent text-blue-sky hover:text-blue-light-05 active:text-blue-dark-03',
    secondary: 'border border-transparent bg-transparent text-neutral-fg hover:underline',
    destructive:
      'border border-transparent bg-transparent text-destructive hover:text-red-light-02 active:text-destructive-heavy',
    success: 'border border-transparent bg-transparent text-success hover:text-success-heavy'
  }
};

const waveColorByVariant: Record<ButtonVariant, Record<ButtonColor, string>> = {
  fill: {
    primary: '[--ds-wave-color:var(--color-orange)]',
    secondary: '[--ds-wave-color:var(--color-disabled-border)]',
    destructive: '[--ds-wave-color:var(--color-destructive)]',
    success: '[--ds-wave-color:var(--color-success)]'
  },
  outline: {
    primary: '[--ds-wave-color:var(--color-orange)]',
    secondary: '[--ds-wave-color:var(--color-disabled-border)]',
    destructive: '[--ds-wave-color:var(--color-destructive)]',
    success: '[--ds-wave-color:var(--color-success)]'
  },
  ghost: {
    primary: '[--ds-wave-color:var(--color-orange)]',
    secondary: '[--ds-wave-color:var(--color-neutral-fg)]',
    destructive: '[--ds-wave-color:var(--color-destructive)]',
    success: '[--ds-wave-color:var(--color-success)]'
  },
  link: {
    primary: '[--ds-wave-color:var(--color-blue-sky)]',
    secondary: '[--ds-wave-color:var(--color-neutral-fg)]',
    destructive: '[--ds-wave-color:var(--color-destructive)]',
    success: '[--ds-wave-color:var(--color-success)]'
  }
};

const disabledClassesByVariant: Record<ButtonVariant, string> = {
  fill: disabledFillClasses,
  outline: disabledOutlineClasses,
  ghost: disabledTextClasses,
  link: disabledTextClasses
};

export function Button({
  variant = 'fill',
  color = 'primary',
  size = 'lg',
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  disabled = false,
  disableWave = false,
  className,
  children,
  ...props
}: ButtonProps) {
  const isInert = disabled || loading;
  const isLink = variant === 'link';
  const showDisabledStyle = disabled && !loading;
  const enableWave = variant !== 'link' && !disableWave;
  const iconSlotClassName = cn(
    'inline-flex shrink-0 items-center justify-center [&>*]:size-full',
    iconSizeClasses[size]
  );

  return (
    <button
      type="button"
      disabled={isInert}
      aria-busy={loading || undefined}
      className={cn(
        baseClasses,
        enableWave && waveButtonClasses,
        variantColorClasses[variant][color],
        enableWave && waveColorByVariant[variant][color],
        isLink ? linkSizeClasses[size] : sizeClasses[size],
        fullWidth && 'w-full',
        showDisabledStyle && disabledClassesByVariant[variant],
        loading && 'cursor-default opacity-65',
        className
      )}
      {...props}
    >
      {startIcon && <span className={iconSlotClassName}>{startIcon}</span>}
      {children}
      {loading ? (
        <span aria-hidden className="inline-flex h-4 shrink-0 items-center">
          <Loading inline size={loadingSizeMap[size]} color="inherit" />
        </span>
      ) : (
        endIcon && <span className={iconSlotClassName}>{endIcon}</span>
      )}
    </button>
  );
}
